/**
 * ClientRouteCanonicalizer — client-side URL canonicalization layer.
 *
 * Mounted ONCE at the AppRoutes root (not per-shell) so it covers every
 * route — including bare routes with no PlatformShell / WorkspaceShell2
 * wrapper (e.g. /clients, /admin/*). Mounting it per-shell previously left
 * those routes uncanonicalized, so a ?client= would linger and could
 * mis-scope a platform page via ScopeContext.
 *
 * Defense-in-depth pair to vercel.json edge redirects:
 *   - Edge handles the mode-dual cold-load redirect (?client= -> /w/:slug/...)
 *     before React mounts. (Same-path ?client= stripping is NOT done at the
 *     edge — it loops; see canonicalizeUrl.ts.)
 *   - This component handles in-app navigation (React Router intercepts
 *     clicks; never hits the edge), same-path stripping, and non-edge
 *     environments (local dev, preview deployments without redirects applied).
 *
 * Behavior (per spec §4.3):
 *   - On non-canonical URL: replace-navigates to the canonical URL,
 *     returns null until the navigation resolves (no flash of wrong shell).
 *   - On already-canonical URL: pure no-op, does NOT block render.
 *
 * Single responsibility: detect non-canonical → issue replace.
 * Does NOT sync state into ClientContext — that's ClientSync.tsx's job,
 * fed by the canonical /w/:slug/... path.
 */

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { canonicalizeUrl } from "@/utils/canonicalizeUrl";

export function ClientRouteCanonicalizer() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const canonical = canonicalizeUrl(location.pathname, params);

  useEffect(() => {
    if (canonical !== null) {
      void navigate(canonical, { replace: true });
    }
  }, [canonical, navigate]);

  // This component renders nothing — it only issues navigation side-effects.
  // The "render-blocking" property in the spec refers to this component's own
  // output (always null), not to gating downstream siblings. Workspace routes
  // are canonical by definition (/w/:slug/...) so the canonicalizer is a
  // no-op there. Edge redirects (vercel.json — Task 4) handle cold loads
  // before React mounts, eliminating the brief paint window on platform
  // routes.
  return null;
}

export default ClientRouteCanonicalizer;
